import { ChangeDetectorRef, Component, EventEmitter, input, Input, OnInit, Output, ViewChild } from '@angular/core';
import { TasksDialogComponent } from '../tasks-dialog/tasks-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { DataBagService } from '@/common/data-bag.service';
import { Data2Service } from '@/common/data2.service';
import { DateService } from '@/common/date.service';
import { InfoboxService } from '@/common/infobox/infobox.service';
import { LoaderService } from '@/common/loader/loader.service';
import { QuestionService } from '@/common/question-dialog/question-dialog.service';
import { TasksService } from '../tasks.service';
import { MatTable } from '@angular/material/table';
import { ConfirmTaskDialogComponent } from './confirm-task-dialog/confirm-task-dialog.component';
import { MatPaginator } from '@angular/material/paginator';
import { TableName } from '@/common/enums/TableName';
import { MatSort } from '@angular/material/sort';
import { ITask } from '../task.model';
import { finalize } from 'rxjs';

enum TaskType {
	GENERAL = 'general',
	DEFAULT = '',
}

@Component({
	selector: 'app-tasks-list',
	templateUrl: './tasks-list.component.html',
	styleUrls: ['./tasks-list.component.scss'],
})
export class TasksListComponent implements OnInit {
	@Input() view = '';
	@ViewChild('dataTable') table: MatTable<any>;
	constructor(
		protected api: Data2Service,
		protected bag: DataBagService,
		protected date: DateService,
		protected infobox: InfoboxService,
		protected route: ActivatedRoute,
		protected router: Router,
		protected question: QuestionService,
		protected loader: LoaderService,
		protected dialog: MatDialog,
		public service: TasksService,
		private readonly changeDetectorRef: ChangeDetectorRef,
	) {}

	@ViewChild(MatPaginator) paginator: MatPaginator;
	@ViewChild(MatSort) sort: MatSort;

	@Input() type = '';
	@Input() tableName = '';
	@Output() setFilters = new EventEmitter<void>();

	readonly tableId = input<number>();

	displayedColumns: string[] = ['created', 'completed', 'users', 'status', 'content', 'notes', 'edit', 'end', 'link'];

	ngOnInit(): void {
		if (this.type !== TaskType.GENERAL) {
			this.service.loadProfile().then(() => {
				this.service.loadTasks(this.tableId().toString());
			});
		}

		this.service.parameters
			.pipe(
				finalize(() => {
					this.setMatSortConfig();
				}),
			)
			.subscribe(() => {
				if (this.tableId()) {
					this.service.dataSource.data = this.service.data;
				} else {
					this.setPaginatorData();
					this.service.parameters.complete();
				}
			});
	}

	private setPaginatorData() {
		this.changeDetectorRef.detectChanges();
		this.translateMatPaginator(this.paginator);
		this.service.dataSource.paginator = this.paginator;
	}

	private setMatSortConfig() {
		this.service.dataSource.sort = this.sort;
		this.service.dataSource.sortingDataAccessor = (item, property) => {
			switch (property) {
				case 'created':
					return new Date(item.createdAt);
				case 'completed':
					return item.completed;
				case 'status':
					return item.status.name;
				case 'notes':
					return item.notesCount;
				default:
					return item[property];
			}
		};

		this.service.dataSource.sort.sort({
			disableClear: false,
			id: 'created',
			start: 'desc',
		});
	}

	protected openReservationInNewTab(task: ITask) {
		const url = this.router.serializeUrl(this.router.createUrlTree([`/reservation/${task.tableId}`]));
		window.open(url, '_blank');
	}

	private translateMatPaginator(paginator: MatPaginator) {
		paginator._intl.firstPageLabel = 'Pierwsza strona';
		paginator._intl.itemsPerPageLabel = 'Produkty na stronę';
		paginator._intl.lastPageLabel = 'Ostatnia strona';
		paginator._intl.nextPageLabel = 'Następna strona';
		paginator._intl.previousPageLabel = 'Poprzednia strona';
	}

	endTask(item) {
		this.dialog
			.open(ConfirmTaskDialogComponent, {
				width: '300px',
			})
			.afterClosed()
			.subscribe((option) => {
				if (option) {
					const dataToSend = {
						deadline: item.deadline,
						completed_by: this.service.loggedUser.id,
						completed: this.date.toUtcIso(new Date()),
						responsibilityDepartments: item.responsibilityDepartments
							? item.responsibilityDepartments.map((organization) => {
									return organization.id;
								})
							: [],
						users: item.users
							? item.users.map((user) => {
									return user.id;
								})
							: [],
						content: item.content,
						multimedia: [],
						table_id:
							this.tableName === TableName.USER_PROFILE ? this.service.loggedUser.id : this.tableId(),
						table_name: this.tableName,
						kinds: item.kinds.map((kind) => kind.id),
						status: 1,
					};
					this.loader.show();
					this.bag.put(`/task_note/task/${item.id}/`, dataToSend, (result, error) => {
						if (error) {
							this.loader.hide();
							return;
						}
						if (this.type !== TaskType.GENERAL) {
							this.service.loadTasks(this.tableId().toString());
						} else this.setFilters.emit();
						this.loader.hide();
					});
					return;
				}
			});
	}

	openTaskDialog(taskId, editable) {
		if (taskId === undefined) {
			this.dialog
				.open(TasksDialogComponent, {
					disableClose: true,
					width: '1000px',
					data: {
						task: taskId,
						editable: editable,
						tableId: this.tableId(),
						tableName: this.tableName,
					},
				})
				.afterClosed()
				.subscribe((task) => {
					if (task === undefined) {
						return;
					}
					this.loader.show();
					this.bag.post(`/task_note/task/`, task, (result, error) => {
						if (error) {
							this.loader.hide();
							return;
						}
						if (this.type !== TaskType.GENERAL) {
							this.service.loadTasks(this.tableId().toString());
						} else this.setFilters.emit();
						this.loader.hide();
					});
				});

			return;
		}
		this.bag.get(`/task_note/task/${taskId}/`, (task, error) => {
			if (error) {
				this.loader.hide();
				return;
			}
			this.dialog
				.open(TasksDialogComponent, {
					disableClose: true,
					width: '1000px',
					data: {
						task: task,
						editable: editable,
						tableId: this.tableId(),
						tableName: this.tableName,
					},
				})
				.afterClosed()
				.subscribe((task) => {
					if (task === undefined) {
						return;
					}
					this.loader.show();
					this.bag.put(`/task_note/task/${taskId}/`, task, (result, error) => {
						if (error) {
							this.loader.hide();
							return;
						}
						if (this.type !== TaskType.GENERAL) {
							this.service.loadTasks(this.tableId().toString());
						} else this.setFilters.emit();
						this.loader.hide();
					});
				});
		});
	}

	protected readonly TaskType = TaskType;
	protected readonly TableName = TableName;
}
